import React, { memo, useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import Dropzone from 'react-dropzone';
import PropTypes from 'prop-types';
import HighlightOff from '@material-ui/icons/HighlightOff';
import {
  SortableContainer,
  SortableElement,
  arrayMove,
} from 'react-sortable-hoc';
import { v4 as uuid } from 'uuid';
import Lottie from 'lottie-react-web';
import {
  ATTACHMENT_TYPES_MAP,
  FILE_INVALID_TYPE,
  FILE_TO_LARGE,
  MEGA_BYTES_1,
} from '../../utils/constants';
import { showNotificationAutoDestroy } from '../ShowNotification';
import pdfFile from '../../img/pdf-file.json';

const DropZoneDragAndDrop = (props) => {
  const { accept, t, fileExtensions, input, maxSize } = props;
  const [listOfFile, setListOfFile] = useState([]);

  const onDrop = (files) => {
    input.onChange(
      files.map((fl, index) =>
        Object.assign(fl, {
          preview: fl.fileId ? fl.preview : URL.createObjectURL(fl),
          id: fl.id ? fl.id : uuid(),
          uploadType: ATTACHMENT_TYPES_MAP.get(fl.type),
          pageOrder: index,
        })
      )
    );
  };

  const onDropRejected = (filesRejected) => {
    if (filesRejected) {
      if (filesRejected[0].errors[0].code === FILE_INVALID_TYPE) {
        showNotificationAutoDestroy(
          'warning',
          t('notifications.attention'),
          `${t('notifications.warning-unsupported-format')} ${fileExtensions}`
        );
      } else if (filesRejected[0].errors[0].code === FILE_TO_LARGE) {
        showNotificationAutoDestroy(
          'warning',
          t('notifications.attention'),
          `${t('notifications.file-large')} ${maxSize / MEGA_BYTES_1} ${t(
            'general.mb'
          )}`
        );
      }
    }
  };

  const removeFile = (index, e) => {
    e.preventDefault();
    const updatedList = [...listOfFile];
    if (updatedList[index].notExclude) {
      showNotificationAutoDestroy(
        'danger',
        t('notifications.attention'),
        t('notifications.not-delete-file')
      );
    } else {
      input.onChange(input.value.filter((val, i) => i !== index));
    }
  };

  const getPreview = (file, i) => {
    let content = <img src={file.preview} alt="Attachment" />;
    if (file.type.startsWith('video')) {
      content = (
        <video src={file.preview} controls>
          <track kind="captions" />
        </video>
      );
    } else if (file.type.startsWith('audio')) {
      content = (
        <audio src={file.preview} controls>
          <track kind="captions" />
        </audio>
      );
    } else if (file.type === 'application/pdf') {
      content = file.notExclude ? (
        <Lottie
          options={{
            animationData: pdfFile,
            autoplay: false,
            loop: false,
          }}
        />
      ) : (
        <iframe
          src={
            file.fileId
              ? `https://docs.google.com/viewer?embedded=true&url=${file.preview}`
              : file.preview
          }
          title={file.name}
        />
      );
    }

    return (
      <>
        <HighlightOff
          className="dropzone__ico-delete"
          onClick={(e) => removeFile(i, e)}
        />
        {content}
      </>
    );
  };

  const SortableItem = SortableElement(({ file, i }) => (
    <div className="dropzone__img">{getPreview(file, i)}</div>
  ));

  const SortableList = SortableContainer(({ items }) => {
    return (
      <div className="d-flex flex-wrap">
        {items.map((value, i) => (
          <SortableItem key={uuid()} index={i} i={i} file={value} />
        ))}
      </div>
    );
  });

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const orderedFiles = arrayMove(input.value, oldIndex, newIndex);
    setListOfFile(orderedFiles);
    onDrop(orderedFiles);
  };

  useEffect(() => {
    setListOfFile(input.value);
  }, [input.value]);

  return (
    <div className="dropzone-drag-and-drop dropzone--multiple">
      <Dropzone
        className="dropzone__input"
        accept={accept}
        name={input.name}
        maxSize={maxSize}
        onDropRejected={onDropRejected}
        onDrop={(filesToUpload) => {
          onDrop(
            input.value ? input.value.concat(filesToUpload) : filesToUpload
          );
        }}
      >
        {({ getRootProps, getInputProps }) => (
          <div {...getRootProps()} className="dropzone__input">
            {(!input.value || input.value.length === 0) && (
              <div className="dropzone__drop-here">
                <span className="lnr lnr-upload" /> {t('general.load-files')}
              </div>
            )}
            <input {...getInputProps()} />
          </div>
        )}
      </Dropzone>
      <div className="dropzone__imgs-wrapper">
        {input.value && Array.isArray(input.value) && (
          <SortableList
            axis="xy"
            pressDelay={100}
            items={input.value}
            onSortEnd={onSortEnd}
          />
        )}
      </div>
    </div>
  );
};

DropZoneDragAndDrop.propTypes = {
  t: PropTypes.func.isRequired,
  input: PropTypes.shape(PropTypes.shape).isRequired,
  fileExtensions: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.shape(PropTypes.shape)),
  ]),
  accept: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.shape(PropTypes.shape)),
  ]),
  maxSize: PropTypes.number,
};

DropZoneDragAndDrop.defaultProps = {
  accept: '',
  fileExtensions: [],
  maxSize: 10000000,
};

export default withTranslation('common')(memo(DropZoneDragAndDrop));
